import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, TrendingUp, Clock, CalendarCheck, Trophy, PhoneCall, MessageSquare, Send, Users, History, Ban, Download, Play, MapPin, ArrowRight, Sparkles, LogOut, Headset, ExternalLink } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api.js';
import toast from 'react-hot-toast';
import { useAuth } from '../store/auth.js';
import { fmtMoney } from '../lib/format.js';
import { openRecordingStream } from '../lib/recordings.js';
import AIOrb from '../components/AIOrb.jsx';
import SetupChecklist from '../components/SetupChecklist.jsx';
import ProducerPageBanner from '../components/producerPage/ProducerPageBanner.jsx';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import { PendingApprovalsCard, PendingSyncRequestsCard } from './Team.jsx';
import { getSocket } from '../lib/socket.js';

function pct(num, den) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

function MetricRow({ label, value, sub, accent = 'purple' }) {
  const accentCls = accent === 'cyan' ? 'text-civix-cyan' : accent === 'emerald' ? 'text-emerald-400' : 'gradient-text';
  return (
    <div className="flex items-baseline justify-between py-3 border-b border-civix-border/40 last:border-0">
      <div className="text-sm text-white/70">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className={`font-display font-bold text-2xl ${accentCls}`}>{value}</div>
        {sub && <div className="text-xs text-white/40">{sub}</div>}
      </div>
    </div>
  );
}

function PeriodCard({ title, period, data, primary = 'purple' }) {
  const dials = data?.dials ?? 0;
  const connects = data?.connects ?? 0;
  const talkOver5 = data?.talkOver5Min ?? 0;
  const booked = data?.bookedAppts ?? 0;
  const presentations = data?.presentations ?? 0;
  const closes = data?.closes ?? 0;
  const premium = data?.premium ?? 0;
  const connectPct = pct(connects, dials);
  const bookPct = pct(booked, connects);
  const showPct = pct(presentations, booked);
  const closePct = pct(closes, presentations);

  return (
    <div className="panel p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-civix-purpleSoft/80">{period}</div>
          <h3 className="font-display font-bold text-lg mt-0.5">{title}</h3>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${primary === 'cyan' ? 'bg-civix-cyan/15 text-civix-cyan' : primary === 'emerald' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-civix-purple/15 text-civix-purple'}`}>
          {primary === 'cyan' ? <Clock className="w-4 h-4" /> : primary === 'emerald' ? <Trophy className="w-4 h-4" /> : <PhoneCall className="w-4 h-4" />}
        </div>
      </div>

      <MetricRow label="Dials" value={dials.toLocaleString()} accent={primary} />
      <MetricRow label="Connects" value={connects.toLocaleString()} sub={`${connectPct}%`} accent="cyan" />
      <MetricRow label="Talk Time > 5 min" value={talkOver5.toLocaleString()} sub={`${pct(talkOver5, connects)}% of connects`} accent="purple" />
      <MetricRow label="Booked Appts" value={booked.toLocaleString()} sub={`${bookPct}% book rate`} accent="cyan" />
      <MetricRow label="Presentations" value={presentations.toLocaleString()} sub={`${showPct}% show rate`} accent="purple" />
      <MetricRow label="Closes" value={closes.toLocaleString()} sub={`${closePct}% close rate`} accent="emerald" />
      <MetricRow label="Premium Written" value={fmtMoney(premium)} accent="emerald" />
    </div>
  );
}

function Sparkline({ data, color = '#a78bfa' }) {
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id={`sp-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.5} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} fill={`url(#sp-${color.slice(1)})`} strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function Dashboard() {
  const { agent, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['dashboard-periods'],
    queryFn: () => apiGet('/dashboard/periods'),
  });
  const { data: pendingData } = useQuery({
    queryKey: ['pending-teammate-approvals'],
    queryFn: () => apiGet('/team/pending-approvals'),
  });

  const pendingApprovals = pendingData?.items || pendingData?.pendingAgents || [];
  const { data: syncReqData } = useQuery({
    queryKey: ['team-sync-requests'],
    queryFn: () => apiGet('/sync/pending'),
  });
  const pendingSyncRequests = syncReqData?.requests || [];

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    function handleAgentRegistered(payload) {
      const name = payload?.agent?.name || payload?.agent?.email || 'A teammate';
      toast(`🔔 ${name} registered and is pending your approval`, { id: `registered-${payload?.agent?._id || name}`, duration: 6000 });
      qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
    }
    function refreshPending() {
      qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
    }
    /** @param {{ message?: string }} payload - `appointment:new` event from a public booking. */
    function handleAppointmentNew(payload) {
      toast(`📅 ${payload?.message || 'New appointment booked'}`, { duration: 8000 });
      qc.invalidateQueries({ queryKey: ['dashboard-periods'] });
      qc.invalidateQueries({ queryKey: ['cal-appts'] });
    }
    socket.on('team:agent-registered', handleAgentRegistered);
    socket.on('team:pending_count_updated', refreshPending);
    /** @param {{ requester?: { name?: string, email?: string } }} payload - `sync:requested` event. */
    function handleSyncRequested(payload) {
      const who = payload?.requester?.name || payload?.requester?.email || 'An agent';
      toast(`🔄 ${who} requested an account sync — review it on your dashboard.`, { duration: 6000 });
      refreshSync();
    }
    function refreshSync() {
      qc.invalidateQueries({ queryKey: ['team-sync-requests'] });
    }
    socket.on('appointment:new', handleAppointmentNew);
    socket.on('sync:requested', handleSyncRequested);
    socket.on('sync:pending_count_updated', refreshSync);
    socket.on('sync:completed', refreshSync);
    socket.on('sync:rejected', refreshSync);
    return () => {
      socket.off('sync:requested', handleSyncRequested);
      socket.off('sync:pending_count_updated', refreshSync);
      socket.off('sync:completed', refreshSync);
      socket.off('sync:rejected', refreshSync);
      socket.off('team:agent-registered', handleAgentRegistered);
      socket.off('team:pending_count_updated', refreshPending);
      socket.off('appointment:new', handleAppointmentNew);
    };
  }, [qc]);

  const daily = data?.daily || {};
  const wtd = data?.wtd || {};
  const mtd = data?.mtd || {};
  const trend = data?.trend14 || Array.from({ length: 14 }).map((_, i) => ({ v: 30 + Math.round(Math.sin(i / 2) * 15 + Math.random() * 10) }));

  // Headline numbers (Daily) — match the screenshot's top row
  const totalCalls = daily.dials || 0;
  const presentations = daily.presentations || 0;
  const revenue = daily.premium || 0;
  const avgDeal = daily.closes ? Math.round((daily.premium || 0) / daily.closes) : 0;

  const needsStates = !agent?.licensedStates?.length;

  return (
    <div className="space-y-6 max-w-[1500px] mx-auto">
      {/* First-login setup walkthrough — auto-hides when complete or dismissed */}
      <SetupChecklist />

      {/* Optional: nudge toward the Producer Validation Page (hides once published or dismissed) */}
      <ProducerPageBanner />

      {/* Unmissable pending-approvals badge for managers & admins */}
      {pendingApprovals.length > 0 && (
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs font-semibold animate-pulse w-fit"
          data-testid="pending-approvals-badge"
        >
          <span className="w-2 h-2 rounded-full bg-rose-500" />
          <span>{pendingApprovals.length} Pending Teammate Approval{pendingApprovals.length > 1 ? 's' : ''}</span>
          <button
            onClick={() => navigate('/settings?tab=team')}
            className="ml-2 underline text-white hover:text-rose-200 cursor-pointer"
          >
            Review &amp; Approve →
          </button>
        </div>
      )}

      {/* Pending Teammate Approvals Safety Gate Card for Managers & Admins */}
      <PendingApprovalsCard
        items={pendingApprovals}
        onApproved={() => qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] })}
      />

      {/* Pending Account Sync Requests safety gate (managers/admins) */}
      <PendingSyncRequestsCard
        items={pendingSyncRequests}
        onApproved={() => qc.invalidateQueries({ queryKey: ['team-sync-requests'] })}
      />

      {/* Aged Leads soft prompt for agents missing licensed states */}
      {needsStates && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-300 text-sm">
          <MapPin className="w-4 h-4 shrink-0" />
          <span className="flex-1">
            Add your licensed states to participate in Aged Leads routing.
          </span>
          <a href="/settings" className="flex items-center gap-1 underline underline-offset-2 font-medium hover:text-amber-100 transition whitespace-nowrap">
            Update Now <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      )}
      <header className="flex items-center justify-between">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Revenue on Autopilot</div>
          <h1 className="font-display text-3xl font-bold mt-1">
            Welcome back, <span className="gradient-text">{(agent?.name || '').split(' ')[0]}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden lg:flex gap-2">
            <a
              href="https://inbounds.civixcrm.ai/booth"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost flex items-center gap-1.5 text-civix-cyan hover:text-white border border-civix-cyan/30 hover:border-civix-cyan transition"
              title="Launch the external live inbound transfer booth"
            >
              <Headset className="w-4 h-4" />Take Inbound Calls<ExternalLink className="w-3 h-3 opacity-60" />
            </a>
            <Link to="/dialer" className="btn-ghost flex items-center gap-2"><Phone className="w-4 h-4"/>Power Dial</Link>
            <Link to="/leads" className="btn-primary flex items-center gap-2"><TrendingUp className="w-4 h-4"/>View All Leads</Link>
          </div>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="btn-ghost flex items-center gap-2 text-white/60 hover:text-rose-400 border border-white/10 hover:border-rose-500/30 transition text-xs px-3 py-2 rounded-lg"
            title="Sign out of CIVIX"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      {/* Hero strip: today snapshot + Civi mascot */}
      <div className="panel p-6 relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center relative z-10">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-civix-purpleSoft/80 mb-2">Today's Snapshot</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="font-display text-4xl font-bold gradient-text">{totalCalls}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Calls</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold gradient-text">{presentations}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Presentations</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold gradient-text">{daily.closes || 0}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Closes</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold gradient-text">{fmtMoney(revenue)}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider mt-1">Revenue</div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-white/50">Connect Rate</div>
                <div className="font-display text-xl font-bold text-civix-cyan">{pct(daily.connects, daily.dials)}%</div>
                <Sparkline data={trend} color="#22d3ee" />
              </div>
              <div>
                <div className="text-xs text-white/50">Book Rate</div>
                <div className="font-display text-xl font-bold gradient-text">{pct(daily.bookedAppts, daily.connects)}%</div>
                <Sparkline data={trend} color="#a78bfa" />
              </div>
              <div>
                <div className="text-xs text-white/50">Close Rate</div>
                <div className="font-display text-xl font-bold text-civix-cyan">{pct(daily.closes, daily.presentations)}%</div>
                <Sparkline data={trend} color="#22d3ee" />
              </div>
              <div>
                <div className="text-xs text-white/50">Avg Deal</div>
                <div className="font-display text-xl font-bold gradient-text">{fmtMoney(avgDeal)}</div>
                <Sparkline data={trend} color="#a78bfa" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 flex justify-center lg:justify-end">
            <AIOrb size={260} />
          </div>
        </div>
        {/* Subtle ambient glow */}
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-civix-purple/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-civix-cyan/10 blur-3xl pointer-events-none" />
      </div>

      {/* The three breakdown panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PeriodCard title="Daily" period="Today" data={daily} primary="purple" />
        <PeriodCard title="Week to Date" period="This Week" data={wtd} primary="cyan" />
        <PeriodCard title="Month to Date" period="This Month" data={mtd} primary="emerald" />
      </div>

      <DashboardExtensions />

      {/* Production funnel summary */}
      <div className="panel p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">Production Funnel</h2>
            <div className="text-xs text-white/50 mt-0.5">Where are your numbers leaking?</div>
          </div>
          <Link to="/reports" className="btn-ghost text-sm">Full Reports</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Dials', value: mtd.dials || 0, color: 'from-civix-purple/30 to-civix-purple/5' },
            { label: 'Connects', value: mtd.connects || 0, color: 'from-civix-cyan/30 to-civix-cyan/5' },
            { label: '5+ Min Talks', value: mtd.talkOver5Min || 0, color: 'from-civix-purple/30 to-civix-purple/5' },
            { label: 'Booked', value: mtd.bookedAppts || 0, color: 'from-civix-cyan/30 to-civix-cyan/5' },
            { label: 'Presented', value: mtd.presentations || 0, color: 'from-civix-purple/30 to-civix-purple/5' },
            { label: 'Closed', value: mtd.closes || 0, color: 'from-emerald-500/30 to-emerald-500/5' },
          ].map((s) => (
            <div key={s.label} className={`rounded-lg p-4 border border-civix-border bg-linear-to-b ${s.color}`}>
              <div className="text-[10px] uppercase tracking-wider text-white/50">{s.label}</div>
              <div className="font-display text-2xl font-bold mt-1">{s.value.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Phase 3 dashboard extensions (#5-#8)
// =============================================================================
function DashboardExtensions() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SmsDripsToday />
        <SmsStats />
        <TurnoverCard />
      </div>
      <AgentSummary />
      <CallHistory />
    </div>
  );
}

function SmsDripsToday() {
  const { data } = useQuery({ queryKey: ['sms-drips-today'], queryFn: () => apiGet('/dashboard/sms-drips-today'), refetchInterval: 60_000 });
  const list = data?.byCampaign || [];
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 mb-3"><Send className="w-4 h-4 text-civix-cyan" /><h3 className="font-display font-bold">SMS Drips Today</h3></div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><div className="text-xs text-white/50">Sent</div><div className="font-display text-2xl gradient-text">{data?.sentToday ?? 0}</div></div>
        <div><div className="text-xs text-white/50">Scheduled</div><div className="font-display text-2xl gradient-text">{data?.scheduledToday ?? 0}</div></div>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">By campaign</div>
      <div className="space-y-1 text-sm">
        {list.length === 0 && <div className="text-white/40 text-xs">No drip activity yet today.</div>}
        {list.map((c) => (
          <div key={String(c._id)} className="flex justify-between text-white/70">
            <span className="truncate pr-2">{c.name || 'Unknown campaign'}</span>
            <span className="font-mono text-civix-cyan">{c.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SmsStats() {
  const { data } = useQuery({ queryKey: ['sms-stats'], queryFn: () => apiGet('/dashboard/sms-stats'), refetchInterval: 120_000 });
  const Row = ({ label, span }) => (
    <div className="flex justify-between py-2 border-b border-white/5 last:border-0">
      <span className="text-white/60 text-sm">{label}</span>
      <div className="flex gap-3 text-sm">
        <span className="text-white/80">↑ {span?.sent || 0}</span>
        <span className="text-civix-cyan">↓ {span?.received || 0}</span>
        <span className="text-emerald-400 font-mono w-10 text-right">{span?.replyRate || 0}%</span>
      </div>
    </div>
  );
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 mb-3"><MessageSquare className="w-4 h-4 text-civix-purple" /><h3 className="font-display font-bold">SMS Stats</h3></div>
      <Row label="Today" span={data?.today} />
      <Row label="This week" span={data?.week} />
      <Row label="This month" span={data?.month} />
      <div className="text-[10px] text-white/40 mt-2">↑ sent · ↓ received · reply rate</div>
    </div>
  );
}

function TurnoverCard() {
  const { data } = useQuery({ queryKey: ['turnover'], queryFn: () => apiGet('/dashboard/turnover-rate?days=30'), refetchInterval: 300_000 });
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-emerald-400" /><h3 className="font-display font-bold">Turnover Rate <span className="text-xs text-white/40">(30d)</span></h3></div>
      <div className="grid grid-cols-2 gap-3">
        <div><div className="text-xs text-white/50">Created</div><div className="font-display text-2xl">{data?.created || 0}</div></div>
        <div><div className="text-xs text-white/50">Contacted</div><div className="font-display text-2xl text-civix-cyan">{data?.contacted || 0}</div></div>
        <div><div className="text-xs text-white/50">Sold</div><div className="font-display text-2xl text-emerald-400">{data?.sold || 0}</div></div>
        <div><div className="text-xs text-white/50">Lost</div><div className="font-display text-2xl text-rose-400">{data?.lost || 0}</div></div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-center pt-3 border-t border-white/5">
        <div><div className="text-xs text-white/50">Turnover</div><div className="font-display text-xl gradient-text">{data?.turnoverRate || 0}%</div></div>
        <div><div className="text-xs text-white/50">Close rate</div><div className="font-display text-xl text-emerald-400">{data?.closeRate || 0}%</div></div>
      </div>
    </div>
  );
}

function AgentSummary() {
  const { data } = useQuery({ queryKey: ['agent-summary'], queryFn: () => apiGet('/dashboard/agent-summary'), refetchInterval: 60_000 });
  const rows = data?.agents || [];
  return (
    <div className="panel p-0 overflow-hidden">
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2"><Users className="w-4 h-4 text-civix-purple" /><h3 className="font-display font-bold">Agent Summary <span className="text-xs text-white/40">(today)</span></h3></div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-white/50 text-xs">
          <tr><th className="px-5 py-2">Agent</th><th>Status</th><th className="text-right">Dials</th><th className="text-right">Connects</th><th className="text-right">SMS</th><th className="text-right">Sold</th><th className="text-right pr-5">Revenue</th></tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-6 text-white/40 text-center">No agents</td></tr>}
          {rows.map((a) => (
            <tr key={a.agentId}>
              <td className="px-5 py-2"><div>{a.name}</div><div className="text-xs text-white/40">{a.email}</div></td>
              <td>{a.available ? <span className="text-emerald-400 text-xs">● available</span> : <span className="text-white/30 text-xs">○ away</span>}</td>
              <td className="text-right font-mono">{a.dials}</td>
              <td className="text-right font-mono text-civix-cyan">{a.connects}</td>
              <td className="text-right font-mono">{a.smsSent}</td>
              <td className="text-right font-mono text-emerald-400">{a.sold}</td>
              <td className="text-right pr-5 font-mono text-emerald-400">${(a.revenue || 0).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function CallHistory() {
  const { data, refetch } = useQuery({ queryKey: ['call-history-today'], queryFn: () => apiGet('/call-history/today/feed'), refetchInterval: 30_000 });
  const calls = data?.calls || [];
  async function blockNumber(id) { if (!confirm('Block this number? All matching leads will be set to DNC.')) return; try { await apiPost(`/call-history/${id}/block-number`, {}); toast.success('Blocked'); refetch(); } catch (e) { toast.error(e.message); } }
  return (
    <div className="panel p-0 overflow-hidden">
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
        <History className="w-4 h-4 text-civix-cyan" /><h3 className="font-display font-bold flex-1">Call History <span className="text-xs text-white/40">(today)</span></h3>
        <Link to="/calls" className="btn-ghost text-xs inline-flex items-center gap-1 text-civix-cyan hover:text-white">
          <PhoneCall className="w-3 h-3" /> View All Calls <ArrowRight className="w-3 h-3" />
        </Link>
        {/* VITE_API_URL already includes the /api prefix (see .env.production).
            Appending /api/... would produce /api/api/... and 404. Path is
            appended raw — audit finding 2026-08-25. */}
        <a href={`${import.meta.env.VITE_API_URL || ''}/call-history/export.csv`} target="_blank" rel="noopener" className="btn-ghost text-xs inline-flex items-center gap-1"><Download className="w-3 h-3" /> CSV</a>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-white/50 text-xs">
          <tr><th className="px-5 py-2">Time</th><th>Lead</th><th>From / To</th><th>Status</th><th className="text-right">Duration</th><th className="text-right pr-5">Actions</th></tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {calls.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-white/40 text-center">No calls today</td></tr>}
          {calls.map((c) => (
            <tr key={c._id}>
              <td className="px-5 py-2 text-white/70">{new Date(c.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
              <td>{c.lead ? `${c.lead.firstName || ''} ${c.lead.lastName || ''}`.trim() || c.lead.phone : '—'}</td>
              <td className="text-white/60 font-mono text-xs">{c.direction === 'inbound' ? c.from : c.to}</td>
              <td><span className={`text-xs px-2 py-0.5 rounded-sm ${c.status === 'completed' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/10 text-white/60'}`}>{c.status}</span></td>
              <td className="text-right font-mono">{c.duration || 0}s</td>
              <td className="text-right pr-5 space-x-2">
                {c.recordingUrl && (
                  <button
                    type="button"
                    onClick={async () => {
                      const url = await openRecordingStream(c._id);
                      if (url) window.open(url, '_blank', 'noopener');
                    }}
                    className="text-civix-cyan hover:text-white inline-flex items-center gap-1 text-xs"
                  >
                    <Play className="w-3 h-3" /> Play
                  </button>
                )}
                <a href={`/scorecards/${c._id}`} className="text-violet-300 hover:text-white inline-flex items-center gap-1 text-xs">Coaching</a>
                <button onClick={() => blockNumber(c._id)} className="text-rose-400 hover:text-rose-300 inline-flex items-center gap-1 text-xs"><Ban className="w-3 h-3" /> Block</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
